import { useFlag } from '@unleash/proxy-client-react'
import { loadProfileAndPlace } from 'app/auth/authAPI'
import MessageAlert from 'app/components/MessageAlert'
import { Routes } from 'app/services/routeService/routeService'
import axios from 'axios'
import { useInterval } from 'libs/useInterval'
import queryString from 'query-string'
import * as R from 'ramda'
import { useCallback, useEffect } from 'react'
import { useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { matchRoutes } from 'react-router-config'
import { withRouter } from 'react-router-dom'
import { setPlace } from '../store/actions/place.actions'
import { AccessType } from './accessControlService'
import {
  useIsAuthorized,
  useIsPlacePendingActivateHasAccess,
} from './authHooks'
import {
  isFreeTierPlaceTeamMemberSignInError,
  teamMemberSignInErrorMessage,
} from './paidPlanAccess'
import {
  logoutFrontEnd,
  logoutUser,
  setSuspendedStatus,
  updateUserRoles,
} from './store/actions'
import { getSuspendErrorStatus, isSuspendError } from './suspendStatus'
import { useAccessControl, usePermissionInfo } from './useAccessControl'

const VIEW_SUBSCRIBER_PATH = { path: '/subscribers/:id' }

const INSPECTION_INTERVAL_TIME = 30 * 1000

const AuthAccessControl = ({ children, routes, history, location }) => {
  const mes_3908_place_profile = useFlag('mes_3908_place_profile')
  const mes_5122 = useFlag('mes_5122_navigation_support_manager_web')

  const { formatMessage } = useIntl()
  const dispatch = useDispatch()

  const guestDefaultURL = Routes.signIn()

  const isMaintenanceDisabled = useSelector(
    ({ maintenanceConfig }) => maintenanceConfig.isMaintenanceDisable,
  )
  const { isGranted: isAnnonymous } = useAccessControl([AccessType.ANNONYMOUS])

  const pathname = location.pathname
  const queryPathParams = queryString.parse(location.search)
  const matched = R.head(matchRoutes(routes, pathname))
  const accesses = R.path(['route', 'accesses'], matched)
  const { isGranted } = useAccessControl(accesses)
  const permissionInfo = usePermissionInfo()
  const isAuthorized = useIsAuthorized()

  const isPendingActiveMenu = useIsPlacePendingActivateHasAccess(accesses)

  const userDefaultURL = isPendingActiveMenu
    ? mes_3908_place_profile
      ? Routes.profileInfoV4()
      : Routes.profileInfo()
    : Routes.notificationTimeline()

  const pushPathname = useCallback(
    (input) => {
      history.push(input)
    },
    [history],
  )
  useEffect(() => {
    if (mes_5122 && pathname === Routes.conversation()) {
      pushPathname(`${process.env.REACT_APP_MANAGER_WEB_HOSTNAME}/chats`)
    }
  }, [mes_5122, pathname, pushPathname])

  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      function (successRes) {
        if (
          successRes.config.url.startsWith(process.env.REACT_APP_API_HOSTNAME)
        ) {
          dispatch({
            type: 'DISABLED_MAINTENANCE',
            value: false,
          })
        }
        return Promise.resolve(successRes)
      },
      function (error) {
        const isErrorData = error && error.response && error.response.data
        if (
          isErrorData &&
          error?.response?.data?.status === 401 &&
          isAuthorized
        ) {
          logoutFrontEnd(dispatch)
          window.location.href = userDefaultURL
        } else if (
          isErrorData &&
          error.response.data.status === 503 &&
          error.response.data.details &&
          error.response.data.details.code === 700
        ) {
          logoutFrontEnd(dispatch)
          dispatch({
            type: 'DISABLED_MAINTENANCE',
            value: true,
          })
          pushPathname({
            pathname: Routes.maintenance(),
          })
        }
        return Promise.reject(error)
      },
    )

    return () => {
      axios.interceptors.response.eject(interceptor)
    }
  }, [dispatch, isAuthorized, pushPathname])

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (R.compose(R.isEmpty, R.prop('permissions'))(permissionInfo)) {
      dispatch(logoutUser())
      return
    }

    if (!isGranted || isPendingActiveMenu) {
      const { redirect } = queryPathParams

      //redirectParam
      const [redirectPath] = redirect
        ? decodeURIComponent(redirect).split('?')
        : ['', '']
      const targetRedirectURL = isAnnonymous
        ? guestDefaultURL
        : redirectPath || userDefaultURL
      const checkMatchRoutes = R.head(
        matchRoutes([VIEW_SUBSCRIBER_PATH], pathname),
      )
      const checkEqualsRoutes = R.equals(targetRedirectURL, pathname)

      if (checkMatchRoutes && !isPendingActiveMenu) {
        pushPathname({
          pathname: Routes.permissionDenied(),
        })
      } else if (checkEqualsRoutes && !isPendingActiveMenu) {
        dispatch(logoutUser())
      } else {
        pushPathname({
          pathname: isMaintenanceDisabled
            ? Routes.maintenance()
            : targetRedirectURL,
        })
      }
    }
  }, [
    isGranted,
    isAnnonymous,
    pushPathname,
    guestDefaultURL,
    userDefaultURL,
    dispatch,
    formatMessage,
    permissionInfo,
    pathname,
  ])
  /* eslint-enable react-hooks/exhaustive-deps */

  useInterval(() => {
    if (!isAuthorized) return
    loadProfileAndPlace()
      .then(({ user, place }) => {
        R.compose(dispatch, updateUserRoles)(user)
        R.compose(dispatch, setPlace)(place)
      })
      .catch((error) => {
        if (isSuspendError(error)) {
          R.compose(dispatch, setSuspendedStatus, getSuspendErrorStatus)(error)
          dispatch(logoutUser())
        } else if (isFreeTierPlaceTeamMemberSignInError(error)) {
          dispatch(logoutUser())
          R.compose(
            dispatch,
            (message) => MessageAlert.showError({ message }),
            teamMemberSignInErrorMessage(formatMessage),
          )(error)
        }
      })
  }, INSPECTION_INTERVAL_TIME)

  return isGranted ? children : null
}

export default withRouter(AuthAccessControl)
